// src/pages/ClientManagementPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, writeBatch, getDoc, getDocs, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';
import TOTPDisplay from '../components/common/TOTPDisplay';
import PasswordGeneratorInput from '../components/common/PasswordGeneratorInput';
import QrCodeInput from '../components/common/QrCodeInput';
import LinkifyText from '../components/common/LinkifyText';
import PasswordDisplay from '../components/common/PasswordDisplay';
import { EQUIPMENT_CONFIG, EQUIPMENT_TYPES } from '../config/equipmentConfig';
import ClientPortalUsersModal from '../components/ClientPortalUsersModal';

const formatCPFouCNPJ = (value, tipoPessoa) => {
    const clean = value.replace(/\D/g, '');
    if (tipoPessoa === 'PF') {
        if (clean.length <= 3) return clean;
        if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
        if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
        return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`;
    } else {
        if (clean.length <= 2) return clean;
        if (clean.length <= 5) return `${clean.slice(0, 2)}.${clean.slice(2)}`;
        if (clean.length <= 8) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5)}`;
        if (clean.length <= 12) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8)}`;
        return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12, 14)}`;
    }
};

const formatPhone = (value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length <= 2) return clean.length > 0 ? `(${clean}` : '';
    if (clean.length <= 6) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
    if (clean.length <= 10) return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7, 11)}`;
};

// --- Componente Principal ---
export default function ClientManagementPage({ pageTitle, collectionName, userId, permissionLevel, allowedCompanies, uiItems }) {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isClientFormOpen, setIsClientFormOpen] = useState(false);
    const [clientFormMode, setClientFormMode] = useState('add'); // 'add' or 'edit'
    
    // Estados para o dropdown pesquisável de clientes
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [clientSearchText, setClientSearchText] = useState('');
    
    // Estados centralizados para Equipamentos e Modais
    const [equipment, setEquipment] = useState([]);
    const [loadingEquipment, setLoadingEquipment] = useState(false);
    const [formMode, setFormMode] = useState(null);
    const [currentItem, setCurrentItem] = useState(null);
    const [categorySelectionMode, setCategorySelectionMode] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showPortalUsersModal, setShowPortalUsersModal] = useState(false);
    const [viewItem, setViewItem] = useState(null);

    const { ConfirmationModal, showConfirmation } = useConfirmationModal();

    const clientsCollectionPath = useMemo(() => `/artifacts/${appId}/public/data/${collectionName}`, [collectionName]);
    const equipmentCollectionPath = useMemo(() => selectedClient ? `/artifacts/${appId}/public/data/${collectionName}/${selectedClient.id}/equipment` : null, [collectionName, selectedClient]);

    // Carregamento de clientes
    useEffect(() => {
        const q = query(collection(db, clientsCollectionPath));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            let data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // FILTRO DE GRUPO
            if (allowedCompanies !== null) {
                data = data.filter(c => allowedCompanies.includes(c.name));
            }

            // Filtro de Situação (Ativo) e Visibilidade em Ativos
            data = data.filter(c => c.ativo !== false && c.visibilidadeModulos?.assets !== false);

            data.sort((a, b) => (a.nomeFantasia || a.name || "").localeCompare(b.nomeFantasia || b.name || ""));
            setClients(data);
            setSelectedClient(prev => {
                if (!prev) return null;
                const updated = data.find(c => c.id === prev.id);
                return updated || null;
            });
            setLoading(false);
        }, (error) => {
            console.error("Erro ao carregar clientes:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [clientsCollectionPath, allowedCompanies]);



    // Carregamento de equipamentos (agora no pai para permitir ações no cabeçalho)
    useEffect(() => {
        if (!equipmentCollectionPath) {
            setEquipment([]);
            return;
        }
        setLoadingEquipment(true);
        const q = query(collection(db, equipmentCollectionPath), orderBy("position", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEquipment(data);
            setLoadingEquipment(false);
        }, (error) => {
            console.error("Erro ao carregar equipamentos:", error);
            setLoadingEquipment(false);
        });
        return () => unsubscribe();
    }, [equipmentCollectionPath]);

    const handleSelectClient = (clientId) => {
        const client = clients.find(c => c.id === clientId);
        setSelectedClient(client || null);
    };

    const handleOpenClientForm = (mode) => {
        setClientFormMode(mode);
        setIsClientFormOpen(true);
    };
    
    const handleMoveClient = async () => {
        if (!selectedClient) return;
        const isContract = collectionName === 'clients';
        const destinationCollectionName = isContract ? 'clients-avulsos' : 'clients';
        const destinationFriendlyName = isContract ? 'Cliente Avulso' : 'Clientes Contrato';

        showConfirmation({
            title: "Mover Cliente?",
            message: `Tem a certeza que deseja mover "${selectedClient.nomeFantasia || selectedClient.name}" para ${destinationFriendlyName}?`,
            onConfirm: async () => {
                try {
                    const sourcePath = `/artifacts/${appId}/public/data/${collectionName}/${selectedClient.id}`;
                    const destinationPath = `/artifacts/${appId}/public/data/${destinationCollectionName}/${selectedClient.id}`;

                    const batch = writeBatch(db);

                    const clientDocSnap = await getDoc(doc(db, sourcePath));
                    if (!clientDocSnap.exists()) return;
                    
                    const equipmentQuery = query(collection(db, `${sourcePath}/equipment`));
                    const equipmentSnapshot = await getDocs(equipmentQuery);

                    batch.set(doc(db, destinationPath), clientDocSnap.data());
                    equipmentSnapshot.forEach(equipDoc => {
                        batch.set(doc(db, `${destinationPath}/equipment/${equipDoc.id}`), equipDoc.data());
                    });

                    equipmentSnapshot.forEach(equipDoc => batch.delete(equipDoc.ref));
                    batch.delete(clientDocSnap.ref);

                    await batch.commit();
                    setSelectedClient(null);
                } catch (error) {
                    console.error("Erro ao mover cliente:", error);
                }
            },
        });
    };

    const handleDeleteClient = async () => {
        if (!selectedClient) return;
        if (equipment.length > 0) {
            showConfirmation({
                title: "Ação Bloqueada",
                message: `Não é possível apagar "${selectedClient.name}" porque possui equipamentos cadastrados. Remova-os primeiro.`,
                confirmText: "OK",
                confirmColor: "bg-cyan-600",
                showCancelButton: false,
            });
            return;
        }

        showConfirmation({
            title: "Excluir Cliente?",
            message: `Tem a certeza que deseja apagar "${selectedClient.name}"? Esta ação é irreversível.`,
            confirmText: "Excluir",
            confirmColor: "bg-red-600",
            showCancelButton: true,
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, clientsCollectionPath, selectedClient.id));
                    setSelectedClient(null);
                } catch(error) {
                    console.error("Erro ao apagar cliente:", error);
                }
            },
        });
    };

    const handleOpenForm = (category, mode = 'add', item = null) => {
        setCategorySelectionMode(false);
        setFormMode({ mode, category });
        setCurrentItem(item);
    };

    const handleDeleteEquipment = (equipmentId) => {
        showConfirmation({
            title: "Excluir Equipamento?",
            message: "Tem certeza que deseja apagar este item?",
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, equipmentCollectionPath, equipmentId));
                } catch (error) {
                    console.error("Erro ao apagar equipamento:", error);
                }
            }
        });
    };

    return (
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-10 pb-8 min-h-screen bg-slate-900 text-slate-100">
            <ConfirmationModal />
            
            {/* Cabeçalho Unificado de Controles */}
            <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 mb-8 border-b border-slate-700/50 pb-6 pt-8 sticky top-0 bg-slate-900 z-30">
                <div className="flex-grow min-w-0">
                    <h1 className="text-3xl font-extrabold text-white tracking-tight flex flex-wrap items-center gap-4">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 whitespace-nowrap">{pageTitle}</span>
                    </h1>
                    <p className="text-slate-500 mt-1.5 text-xs font-medium uppercase tracking-widest opacity-70">Intranet Arcus • Gestão Técnica de Infraestrutura</p>
                </div>

                <div className="flex flex-col md:flex-row items-end gap-6">
                    {/* Seletor de Cliente integrado */}
                    <div className="w-full md:w-80 group">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 ml-1 transition-colors group-focus-within:text-cyan-400">Cliente / Registro Selecionado</label>
                        <div className="relative flex gap-2">
                            <div className="relative flex-grow">
                                {loading ? (
                                    <div className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 px-4 text-slate-400 font-bold text-sm">A carregar base de dados...</div>
                                ) : (
                                    <div className="relative w-full">
                                        {isClientDropdownOpen && (
                                            <div 
                                                className="fixed inset-0 z-40" 
                                                onClick={() => setIsClientDropdownOpen(false)}
                                            ></div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                                            className="w-full flex items-center justify-between bg-slate-800 border border-slate-700 rounded-xl py-3 pl-4 pr-4 focus:ring-2 focus:ring-cyan-500/50 focus:outline-none text-white font-bold transition-all cursor-pointer hover:border-slate-500 shadow-2xl text-left z-50 relative"
                                        >
                                            <span className="truncate">
                                                {selectedClient 
                                                    ? `${selectedClient.nomeFantasia || selectedClient.name}${selectedClient.ativo === false ? ' (Inativo)' : ''}`
                                                    : '-- Selecione um Cliente --'}
                                            </span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400 shrink-0 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>

                                        {isClientDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[400px]">
                                                <div className="p-3 border-b border-slate-700">
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        placeholder="Pesquisar cliente..."
                                                        value={clientSearchText}
                                                        onChange={(e) => setClientSearchText(e.target.value)}
                                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg py-2 px-3 text-white text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 placeholder-slate-500 font-sans"
                                                    />
                                                </div>
                                                <div className="overflow-y-auto custom-scrollbar flex-1">
                                                    {clients.filter(client => {
                                                        const search = clientSearchText.toLowerCase();
                                                        return (client.nomeFantasia || client.name).toLowerCase().includes(search);
                                                    }).map(client => (
                                                        <button
                                                            key={client.id}
                                                            onClick={() => {
                                                                handleSelectClient(client.id);
                                                                setIsClientDropdownOpen(false);
                                                                setClientSearchText('');
                                                            }}
                                                            className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-slate-700/50 last:border-none hover:bg-slate-700 ${selectedClient?.id === client.id ? 'bg-cyan-900/30 text-cyan-400 font-bold' : 'text-slate-200'}`}
                                                        >
                                                            {client.nomeFantasia || client.name}{client.ativo === false ? ' (Inativo)' : ''}
                                                        </button>
                                                    ))}
                                                    {clients.filter(client => {
                                                        const search = clientSearchText.toLowerCase();
                                                        return (client.nomeFantasia || client.name).toLowerCase().includes(search);
                                                    }).length === 0 && (
                                                        <div className="p-4 text-center text-slate-500 text-sm">Nenhum cliente encontrado</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Ações Rápidas */}
                    {selectedClient && (
                        <div className="flex items-center gap-3 animate-in slide-in-from-right-8 duration-500">
                            {permissionLevel === 'edit' && (
                                <button onClick={() => setCategorySelectionMode(true)} className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-5 rounded-xl transition-all shadow-xl shadow-cyan-900/20 hover:shadow-cyan-500/30 flex items-center gap-2.5 text-sm active:scale-95 group/btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                                    Novo Item
                                </button>
                            )}
                            <button onClick={() => setShowReportModal(true)} title="Gerar Relatório de Impressão" className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold py-3 px-5 rounded-xl transition-all border border-slate-700 flex items-center gap-2.5 text-sm shadow-xl active:scale-95">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                Relatório
                            </button>
                            <button onClick={() => setShowPortalUsersModal(true)} title="Gerenciar Usuários com Acesso ao Portal Web" className="bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold py-3 px-4 rounded-xl transition-all border border-slate-700 flex items-center gap-2 text-sm shadow-xl active:scale-95">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                Acessos Web
                            </button>
                            {permissionLevel === 'edit' && (
                                <div className="flex items-center gap-2 border-l border-slate-700 pl-4 ml-1">
                                    <button onClick={handleMoveClient} title="Mover para outro setor administrativo" className="p-2.5 text-slate-500 hover:text-cyan-400 hover:bg-slate-800 rounded-xl transition-all active:scale-90 border border-transparent hover:border-slate-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            {/* Conteúdo Principal (Grade de Equipamentos) */}
            {selectedClient ? (
                <div className="animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
                    <ClientDetailPage 
                        equipment={equipment}
                        loadingEquipment={loadingEquipment}
                        setEquipment={setEquipment}
                        equipmentCollectionPath={equipmentCollectionPath}
                        setViewItem={setViewItem}
                        permissionLevel={permissionLevel}
                        uiItems={uiItems}
                    />
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-32 text-center animate-in fade-in duration-1000">
                    <div className="bg-slate-800/30 p-12 rounded-3xl border border-dashed border-slate-700 mb-8 backdrop-blur-sm">
                        <div className="text-slate-600 mb-6 flex justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-400 mb-2">Painel de Monitoramento</h2>
                        <p className="text-slate-500 max-w-sm">Selecione um cliente no seletor acima para visualizar e gerenciar seu inventário técnico de equipamentos.</p>
                    </div>
                </div>
            )}

            {/* Modais centralizados no pai */}
            {categorySelectionMode && (
                 <div className="fixed inset-0 bg-slate-950/80 flex justify-center items-center z-50 p-4 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-slate-800 rounded-3xl p-8 shadow-2xl w-full max-w-2xl border border-slate-700 animate-in zoom-in-95 duration-300">
                        <h3 className="text-2xl font-bold text-cyan-300 mb-8 text-center tracking-tight">Qual tipo de equipamento deseja adicionar?</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                            {EQUIPMENT_TYPES.filter(key => !uiItems || uiItems.includes(key)).map(key => (
                                <button key={key} onClick={() => handleOpenForm(key)} className="flex flex-col items-center justify-center gap-4 p-7 bg-slate-900/40 hover:bg-slate-700/60 rounded-2xl transition-all border border-slate-700/50 hover:border-cyan-500/50 group hover:shadow-2xl hover:shadow-cyan-500/10">
                                    <div className="text-slate-500 group-hover:text-cyan-400 transition-all group-hover:scale-110">
                                        {EQUIPMENT_CONFIG[key].icon}
                                    </div>
                                    <span className="font-bold text-slate-400 group-hover:text-white transition-colors text-sm">{key}</span>
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setCategorySelectionMode(false)} className="mt-10 w-full py-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-2xl font-bold transition-all hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 border border-transparent">Cancelar</button>
                    </div>
                 </div>
            )}

            {formMode && (
                <EquipmentFormModal 
                    mode={formMode.mode}
                    type={formMode.category}
                    item={currentItem}
                    onClose={() => setFormMode(null)}
                    collectionPath={equipmentCollectionPath}
                />
            )}

            {showReportModal && selectedClient && (
                <ReportModal 
                    client={selectedClient} 
                    equipment={equipment} 
                    onClose={() => setShowReportModal(false)} 
                />
            )}

            {showPortalUsersModal && selectedClient && (
                <ClientPortalUsersModal 
                    client={selectedClient} 
                    collectionName={collectionName} 
                    onClose={() => setShowPortalUsersModal(false)} 
                    permissionLevel={permissionLevel}
                />
            )}

            {isClientFormOpen && (
                <ClientFormModal
                    mode={clientFormMode}
                    client={clientFormMode === 'edit' ? selectedClient : null}
                    onClose={() => setIsClientFormOpen(false)}
                    collectionName={collectionName}
                />
            )}



            {viewItem && (
                <ItemDetailViewModal 
                    item={viewItem} 
                    onClose={() => setViewItem(null)} 
                    onEdit={() => { 
                        const itemToEdit = viewItem;
                        setViewItem(null); 
                        handleOpenForm(itemToEdit.type, 'edit', itemToEdit); 
                    }}
                    onDelete={() => {
                        const idToDelete = viewItem.id;
                        setViewItem(null);
                        handleDeleteEquipment(idToDelete);
                    }}
                    permissionLevel={permissionLevel}
                />
            )}
        </div>
    );
}

// --- Componentes auxiliares ---

function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Falha ao copiar:', err);
        }
    };

    return (
        <button 
            onClick={handleCopy}
            title={copied ? 'Texto copiado!' : `Copiar conteúdo de ${label}`}
            className={`p-2 rounded-lg transition-all flex items-center justify-center shrink-0 border ${copied ? 'bg-green-500/20 text-green-400 border-green-500/30 scale-110' : 'bg-slate-800/50 hover:bg-slate-700 text-slate-500 hover:text-cyan-400 border-slate-700 hover:border-slate-500 opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
        >
            {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            )}
        </button>
    );
}

// --- Novo Modal de Visualização de Item ---
function ItemDetailViewModal({ item, onClose, onEdit, onDelete, permissionLevel }) {
    if (!item) return null;

    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <header className="p-7 border-b border-slate-800 bg-slate-800/20 flex justify-between items-start">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.2em] bg-cyan-400/10 px-2 py-0.5 rounded border border-cyan-400/20">{item.type}</span>
                        </div>
                        <h3 className="text-3xl font-extrabold text-white break-words pr-4">{item.data.nome || 'Recurso Sem Nome'}</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-2.5 rounded-xl hover:bg-slate-800 transition-all focus:outline-none border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <div className="p-8 overflow-y-auto space-y-6 custom-scrollbar bg-slate-900/50">
                    <div className="grid grid-cols-1 gap-5">
                        {EQUIPMENT_CONFIG[item.type]?.fields
                            .filter(field => item.data[field.name] && field.name !== 'nome' && field.name !== 'totpSecret')
                            .map((field) => {
                                const key = field.name;
                                const value = item.data[key];
                                let displayValue = value;
                                if (typeof value === 'object' && value !== null) {
                                    if (value.seconds) {
                                        try {
                                            displayValue = new Date(value.seconds * 1000).toLocaleString('pt-BR');
                                        } catch (e) {
                                            displayValue = 'Data Inválida';
                                        }
                                    } else {
                                        displayValue = JSON.stringify(value);
                                    }
                                }

                                const label = field.label || key;

                                return (
                                    <div key={key} className="bg-slate-800/30 p-5 rounded-2xl border border-slate-700/30 group hover:border-slate-600 transition-all hover:bg-slate-800/50">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="min-w-0 flex-grow">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">{label}</label>
                                                <span className="text-lg font-bold text-white whitespace-pre-wrap break-words block leading-snug">
                                                    {(key === 'senha' || key.toLowerCase().includes('password')) ? (
                                                        <PasswordDisplay value={displayValue} />
                                                    ) : (
                                                        <LinkifyText text={String(displayValue || '')} />
                                                    )}
                                                </span>
                                            </div>
                                            <CopyButton value={displayValue} label={label} />
                                        </div>
                                    </div>
                                );
                            })
                        }

                        {item.data.customFields && item.data.customFields.length > 0 && (
                            <div className="pt-6 mt-2 border-t border-slate-800 space-y-5">
                                <h4 className="text-xs font-black text-slate-600 uppercase tracking-[0.3em] flex items-center gap-2">
                                    <span className="w-8 h-px bg-slate-800"></span>
                                    Campos Dinâmicos
                                </h4>
                                {item.data.customFields.map((field, index) => field.name && (
                                    <div key={`custom-${index}`} className="bg-slate-800/30 p-5 rounded-2xl border border-slate-700/30 group hover:border-slate-600 transition-all hover:bg-slate-800/50">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="min-w-0 flex-grow">
                                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">{field.name}</label>
                                                <span className="text-lg font-bold text-white whitespace-pre-wrap break-words block leading-snug">
                                                    <LinkifyText text={String(field.value || '')} />
                                                </span>
                                            </div>
                                            <CopyButton value={field.value} label={field.name} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="pt-2">
                             {item.data.totpSecret && (
                                <div className="space-y-4">
                                    <QrCodeInput 
                                        value={item.data.totpSecret} 
                                        onChange={() => {}} 
                                        placeholder="Chave 2FA"
                                        readOnly={true}
                                    />
                                    <div className="bg-cyan-500/5 p-6 rounded-2xl border border-cyan-500/10">
                                        <TOTPDisplay secret={item.data.totpSecret} />
                                    </div>
                                </div>
                             )}
                        </div>
                    </div>
                </div>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/20 flex flex-col sm:flex-row gap-4">
                    {permissionLevel === 'edit' && (
                        <div className="flex flex-grow gap-3">
                            <button 
                                onClick={onEdit} 
                                className="flex-grow bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-6 rounded-2xl transition-all shadow-xl shadow-cyan-900/40 flex items-center justify-center gap-2 group/edit"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                                <span className="tracking-tight">Editar Registro</span>
                            </button>
                            <button 
                                onClick={onDelete} 
                                className="bg-red-500/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-500/30 font-bold py-3 px-5 rounded-2xl transition-all flex items-center justify-center min-w-[60px] group/delete"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover/delete:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </div>
                    )}
                    <button 
                        onClick={onClose} 
                        className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-4 px-8 rounded-2xl transition-all sm:w-auto"
                    >
                        Sair
                    </button>
                </footer>
            </div>
        </div>
    );
}

// --- Componente de Grade de Itens ---
function ClientDetailPage({ equipment, loadingEquipment, setEquipment, equipmentCollectionPath, setViewItem, permissionLevel, uiItems }) {
    const [draggedItem, setDraggedItem] = useState(null);
    const [searchQueries, setSearchQueries] = useState({});

    const groupedEquipment = useMemo(() => {
        const grouped = equipment.reduce((acc, item) => {
            const type = item.type;
            if (uiItems && !uiItems.includes(type)) return acc;
            if (!acc[type]) acc[type] = [];
            acc[type].push(item);
            return acc;
        }, {});
        return grouped;
    }, [equipment, uiItems]);

    const getFilteredItems = (type) => {
        const items = groupedEquipment[type] || [];
        const query = (searchQueries[type] || '').toLowerCase().trim();
        if (!query) return items;
        return items.filter(item => {
            const nome = (item.data.nome || '').toLowerCase();
            const ip = (item.data.ip || '').toLowerCase();
            return nome.includes(query) || ip.includes(query);
        });
    };

    const handleDragStart = (e, item) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };
    
    const handleDrop = async (e, targetItem) => {
        e.preventDefault();
        if (!draggedItem || draggedItem.id === targetItem.id || draggedItem.type !== targetItem.type) {
            setDraggedItem(null);
            return;
        }

        const currentList = [...equipment];
        const draggedIndex = currentList.findIndex(i => i.id === draggedItem.id);
        const targetIndex = currentList.findIndex(i => i.id === targetItem.id);

        const [reorderedItem] = currentList.splice(draggedIndex, 1);
        currentList.splice(targetIndex, 0, reorderedItem);
        setEquipment(currentList);
        
        try {
            const batch = writeBatch(db);
            const itemsToUpdate = currentList.filter(item => item.type === draggedItem.type);

            itemsToUpdate.forEach((item, index) => {
                const docRef = doc(db, equipmentCollectionPath, item.id);
                batch.update(docRef, { position: index });
            });

            await batch.commit();
        } catch (error) {
            console.error("Erro ao reordenar equipamentos:", error);
        }
        setDraggedItem(null);
    };

    if (loadingEquipment) {
        return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-14 w-14 border-b-2 border-cyan-500 shadow-xl shadow-cyan-500/20"></div></div>;
    }

    const types = Object.keys(groupedEquipment).sort();

    if (types.length === 0) {
        return (
            <div className="flex justify-center py-10 opacity-40">
                <p className="font-bold tracking-widest uppercase text-slate-500">Este cliente ainda não possui equipamentos cadastrados.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-8">
            {types.map(type => {
                const filteredItems = getFilteredItems(type);
                return (
                    <div key={type} className="bg-slate-800/95 rounded-[2rem] border border-slate-700/80 flex flex-col h-[520px] overflow-hidden transition-all hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-cyan-500/10 group/card shadow-xl">
                        <div className="p-6 border-b border-slate-700/50 bg-slate-900/40 flex items-center justify-between gap-4">
                            <h2 className="text-xl font-extrabold text-cyan-300 truncate flex items-center gap-3 shrink-0">
                                <div className="text-cyan-400 opacity-60 group-hover/card:opacity-100 transition-opacity">
                                    {EQUIPMENT_CONFIG[type]?.icon || <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>}
                                </div>
                                {type}
                            </h2>
                            
                            <div className="relative flex-grow max-w-[180px]">
                                <input 
                                    type="text"
                                    placeholder="Pesquisar..."
                                    value={searchQueries[type] || ''}
                                    onChange={(e) => setSearchQueries(prev => ({ ...prev, [type]: e.target.value }))}
                                    className="w-full bg-slate-900/60 border border-slate-700/50 hover:border-cyan-500/30 focus:border-cyan-500 rounded-lg py-1 pl-3 pr-8 text-xs text-white placeholder-slate-500 focus:outline-none transition-all"
                                />
                                {searchQueries[type] && (
                                    <button 
                                        onClick={() => setSearchQueries(prev => ({ ...prev, [type]: '' }))}
                                        className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-500 hover:text-white"
                                    >
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            <span className="bg-slate-900/80 text-cyan-400 text-[10px] font-black px-3 py-1 rounded-lg border border-cyan-500/20 shadow-inner shrink-0">
                                {searchQueries[type] ? `${filteredItems.length}/${groupedEquipment[type].length}` : groupedEquipment[type].length} ITENS
                            </span>
                        </div>
                        
                        <div className="flex-grow overflow-y-auto p-5 custom-scrollbar bg-slate-900/10">
                            <div className="flex flex-col gap-3">
                                {filteredItems.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500 text-xs font-bold uppercase tracking-wider">
                                        Nenhum item encontrado
                                    </div>
                                ) : (
                                    filteredItems.map((item) => (
                                        <div 
                                            key={item.id}
                                            draggable={permissionLevel === 'edit'}
                                            onDragStart={(e) => handleDragStart(e, item)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, item)}
                                            onClick={() => setViewItem(item)}
                                            className={`group relative bg-slate-800/80 border border-slate-700/60 p-5 rounded-2xl cursor-pointer transition-all hover:bg-slate-700 hover:border-cyan-500/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/40 active:scale-[0.98] ${draggedItem?.id === item.id ? 'opacity-30 border-dashed' : ''}`}
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex flex-col gap-1 min-w-0">
                                                    <span className="text-slate-100 font-extrabold truncate text-base leading-tight group-hover:text-cyan-100">{item.data.nome || 'Sem Nome'}</span>
                                                    {item.data.ip && <span className="text-[10px] text-slate-500 font-mono tracking-wider font-bold group-hover:text-cyan-400/70 transition-colors uppercase">IP: {item.data.ip}</span>}
                                                </div>
                                                <div className="bg-slate-700/40 p-2 rounded-xl text-slate-500 group-hover:text-cyan-400 group-hover:bg-cyan-400/10 transition-all border border-transparent group-hover:border-cyan-500/20">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// --- Modal de Relatório ---
function ReportModal({ client, equipment, onClose }) {
    const [selectedIds, setSelectedIds] = useState(new Set(equipment.map(item => item.id)));

    const allGrouped = useMemo(() => {
        return equipment.reduce((acc, item) => {
            const type = item.type;
            if (!acc[type]) acc[type] = [];
            acc[type].push(item);
            return acc;
        }, {});
    }, [equipment]);

    const selectedGrouped = useMemo(() => {
        const grouped = {};
        equipment.filter(item => selectedIds.has(item.id)).forEach(item => {
            const type = item.type;
            if (!grouped[type]) grouped[type] = [];
            grouped[type].push(item);
        });
        return grouped;
    }, [equipment, selectedIds]);

    const toggleItem = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.add(id);
        setSelectedIds(newSelected);
    };

    const toggleCategory = (type) => {
        const categoryItems = allGrouped[type];
        const allSelected = categoryItems.every(item => selectedIds.has(item.id));
        const newSelected = new Set(selectedIds);
        
        categoryItems.forEach(item => {
            if (allSelected) newSelected.delete(item.id);
            else newSelected.add(item.id);
        });
        setSelectedIds(newSelected);
    };

    const handlePrint = () => {
        const printContent = document.getElementById('printable-report-content').innerHTML;
        const printWindow = window.open('', '_blank', 'height=800,width=1000');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Inventário Técnico - ${client.name}</title>
                    <style>
                        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 40px; color: #1e293b; background: #fff; }
                        .header { border-bottom: 3px solid #0891b2; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
                        h1 { font-size: 28px; margin: 0; color: #0f172a; }
                        .timestamp { font-size: 12px; color: #64748b; font-weight: bold; }
                        h2 { font-size: 20px; color: #0891b2; background: #f0f9ff; padding: 10px 15px; border-radius: 8px; margin-top: 35px; border-left: 5px solid #0891b2; page-break-after: avoid; }
                        .item-box { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin-bottom: 20px; page-break-inside: avoid; background: #fafafa; }
                        .item-title { font-size: 18px; font-weight: 800; margin: 0 0 15px 0; color: #1e293b; }
                        .grid { display: grid; grid-template-cols: 1fr 1fr; gap: 15px; }
                        .field { margin-bottom: 5px; }
                        .label { font-size: 10px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
                        .value { font-size: 14px; font-weight: 600; color: #334155; }
                        .footer { margin-top: 50px; text-align: center; font-size: 10px; color: #cbd5e1; border-top: 1px solid #f1f5f9; padding-top: 20px; }
                        @media print { body { margin: 20mm; } .item-box { break-inside: avoid; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div>
                            <div class="label">Relatório de Inventário</div>
                            <h1>${client.name}</h1>
                        </div>
                        <div class="timestamp">Gerado em: ${new Date().toLocaleString('pt-BR')}</div>
                    </div>
                    ${Object.keys(selectedGrouped).sort().map(type => `
                        <h2>${type}</h2>
                        ${selectedGrouped[type].map(item => `
                            <div class="item-box">
                                <div class="item-title">${item.data.nome || 'Sem Nome'}</div>
                                <div class="grid">
                                    ${EQUIPMENT_CONFIG[item.type]?.fields
                                        .filter(field => item.data[field.name] && field.name !== 'nome')
                                        .map(field => `
                                            <div class="field">
                                                <div class="label">${field.label}</div>
                                                <div class="value">${item.data[field.name]}</div>
                                            </div>
                                        `).join('')}
                                    ${(item.data.customFields || []).map(f => `
                                        <div class="field">
                                            <div class="label">${f.name}</div>
                                            <div class="value">${f.value}</div>
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        `).join('')}
                    `).join('')}
                    <div class="footer">Intranet Arcus • Sistema de Gestão de Infraestrutura • Documento Confidencial</div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-50 p-6 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-[95vw] max-h-[92vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
                <header className="p-8 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
                    <div>
                        <h3 className="text-3xl font-black text-white tracking-tight">Personalização do Relatório</h3>
                        <p className="text-slate-500 text-sm mt-1">Selecione os itens e categorias para compor o inventário de {client.name}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-3 rounded-2xl hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <div className="flex flex-grow overflow-hidden">
                    {/* Painel de Seleção Lateral */}
                    <aside className="w-80 border-r border-slate-800/50 bg-slate-900/50 p-6 overflow-y-auto custom-scrollbar shrink-0">
                        <h4 className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                            Estrutura do Relatório
                        </h4>
                        
                        <div className="space-y-6">
                            {Object.keys(allGrouped).sort().map(type => {
                                const categoryItems = allGrouped[type];
                                const allSelected = categoryItems.every(item => selectedIds.has(item.id));
                                const someSelected = categoryItems.some(item => selectedIds.has(item.id));

                                return (
                                    <div key={type} className="space-y-3">
                                        <div 
                                            onClick={() => toggleCategory(type)}
                                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border ${allSelected ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-100' : someSelected ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-900/50 border-slate-800 text-slate-500 opacity-60'}`}
                                        >
                                            <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all ${allSelected ? 'bg-cyan-500 border-cyan-400' : someSelected ? 'bg-slate-700 border-slate-600' : 'bg-transparent border-slate-700'}`}>
                                                {allSelected ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                ) : someSelected && (
                                                    <div className="w-2.5 h-0.5 bg-cyan-400" />
                                                )}
                                            </div>
                                            <span className="font-bold text-xs uppercase tracking-widest truncate flex-grow">{type}</span>
                                            <span className="text-[9px] font-black opacity-50 bg-black/20 px-2 py-0.5 rounded-md">{categoryItems.length}</span>
                                        </div>

                                        <div className="pl-4 space-y-2 border-l border-slate-800 ml-5">
                                            {categoryItems.map(item => {
                                                const isSelected = selectedIds.has(item.id);
                                                return (
                                                    <div 
                                                        key={item.id}
                                                        onClick={() => toggleItem(item.id)}
                                                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all group ${isSelected ? 'text-slate-200' : 'text-slate-600'}`}
                                                    >
                                                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${isSelected ? 'bg-cyan-500/20 border-cyan-500/50' : 'bg-slate-900 border-slate-700 group-hover:border-slate-500'}`}>
                                                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />}
                                                        </div>
                                                        <span className="text-[11px] font-medium truncate">{item.data.nome || 'Sem Nome'}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </aside>

                    {/* Preview Area */}
                    <div className="flex-grow overflow-y-auto p-12 custom-scrollbar bg-slate-50 rounded-tl-[3rem] shadow-inner relative">
                        <div className="absolute top-8 right-12 text-[10px] font-black text-slate-300 uppercase tracking-[0.3em] pointer-events-none">Visualização Realtime</div>
                        
                        <div id="printable-report-content" className="max-w-4xl mx-auto">
                            {Object.keys(selectedGrouped).length === 0 ? (
                                <div className="py-32 flex flex-col items-center justify-center text-center opacity-30 grayscale">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                    <h4 className="text-xl font-bold text-slate-800 uppercase tracking-tighter">Relatório Vazio</h4>
                                    <p className="text-slate-600 max-w-xs mt-2 font-medium">Selecione pelo menos um item na lateral para gerar o documento.</p>
                                </div>
                            ) : (
                                <div className="text-slate-900 font-sans">
                                    <div className="border-b-4 border-cyan-600 pb-8 flex justify-between items-end mb-12">
                                        <div>
                                            <span className="text-[10px] font-black text-cyan-600 uppercase tracking-[0.3em]">Cliente Selecionado</span>
                                            <h1 className="text-5xl font-black mt-2 tracking-tighter">{client.name}</h1>
                                        </div>
                                        <div className="text-right text-xs text-slate-400 font-bold bg-slate-100 px-4 py-2 rounded-full">
                                            {new Date().toLocaleDateString('pt-BR')}
                                        </div>
                                    </div>

                                    {Object.keys(selectedGrouped).sort().map(type => (
                                        <div key={type} className="mb-14 last:mb-0">
                                            <h2 className="text-xl font-black text-cyan-600 border-l-8 border-cyan-600 pl-4 bg-cyan-50/50 py-3 rounded-r-xl mb-8 uppercase tracking-tight">{type}</h2>
                                            <div className="space-y-8">
                                                {selectedGrouped[type].map(item => (
                                                    <div key={item.id} className="border-2 border-slate-100 rounded-[2rem] p-8 bg-slate-50/30 hover:border-cyan-100 transition-colors">
                                                        <h3 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-cyan-500" />
                                                            {item.data.nome || 'Sem Nome'}
                                                        </h3>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12">
                                                            {EQUIPMENT_CONFIG[item.type]?.fields
                                                                .filter(field => item.data[field.name] && field.name !== 'nome')
                                                                .map(field => (
                                                                    <div key={field.name} className="group">
                                                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 transition-colors group-hover:text-cyan-500">{field.label}</div>
                                                                        <div className="text-base font-bold text-slate-700 leading-tight">{item.data[field.name]}</div>
                                                                    </div>
                                                                ))}
                                                            {(item.data.customFields || []).map((f, idx) => (
                                                                <div key={idx} className="group">
                                                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5 transition-colors group-hover:text-cyan-500">{f.name}</div>
                                                                    <div className="text-base font-bold text-slate-700 leading-tight">{f.value}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <div className="mt-20 pt-10 border-t border-slate-100 text-center space-y-2 opacity-40">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Intranet Arcus Systems</p>
                                        <p className="text-[8px] font-bold text-slate-300 uppercase underline decoration-slate-200 decoration-2 underline-offset-4">Documento Confidencial de Infraestrutura Técnica</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/40 flex justify-between items-center bg-slate-900">
                    <div className="flex items-center gap-4 text-slate-500 text-[10px] font-black uppercase tracking-widest pl-4">
                        <span className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-cyan-500" />
                            {selectedIds.size} Itens Selecionados
                        </span>
                        <span className="opacity-20">|</span>
                        <span>Total de {equipment.length} Disponíveis</span>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={onClose} 
                            className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 px-8 rounded-2xl transition-all border border-slate-700 hover:border-slate-500"
                        >
                            Cancelar
                        </button>
                        <button 
                            onClick={handlePrint} 
                            disabled={selectedIds.size === 0}
                            className={`font-black py-4 px-10 rounded-2xl transition-all shadow-2xl flex items-center gap-3 group/print ${selectedIds.size === 0 ? 'bg-slate-800 text-slate-600 cursor-not-allowed border border-slate-700' : 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-cyan-900/40'}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-transform group-hover/print:-translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                            Gerar Relatório Selecionado
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

// --- Modal de Formulário Genérico para Equipamentos ---
function EquipmentFormModal({ mode, type, item, onClose, collectionPath }) {
    const config = EQUIPMENT_CONFIG[type];
    const [loading, setLoading] = useState(false);
    const [customFields, setCustomFields] = useState(item?.data?.customFields || []);
    
    // Inicializar o formulário com dados existentes ou vazios
    const initialFormData = useMemo(() => {
        const data = {};
        config.fields.forEach(f => {
            data[f.name] = item?.data?.[f.name] || '';
        });
        return data;
    }, [config.fields, item]);

    const [formData, setFormData] = useState(initialFormData);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddCustomField = () => {
        setCustomFields([...customFields, { name: '', value: '' }]);
    };

    const handleCustomFieldChange = (index, field, value) => {
        const updated = [...customFields];
        updated[index][field] = value;
        setCustomFields(updated);
    };

    const handleRemoveCustomField = (index) => {
        setCustomFields(customFields.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const dataToSave = {
                type,
                data: {
                    ...formData,
                    customFields: customFields.filter(f => f.name.trim() !== '')
                },
                updatedAt: new Date()
            };

            if (mode === 'add') {
                dataToSave.createdAt = new Date();
                dataToSave.position = 0; // O sistema de drag/drop ajusta depois se necessário
                await addDoc(collection(db, collectionPath), dataToSave);
            } else {
                await updateDoc(doc(db, collectionPath, item.id), dataToSave);
            }
            onClose();
        } catch (error) {
            console.error("Erro ao salvar equipamento:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <header className="p-7 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-black text-white">{mode === 'add' ? 'Novo Cadastro' : 'Editar Registro'}</h3>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">{type}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-all focus:outline-none border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-8 space-y-8 custom-scrollbar">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {config.fields.map(field => (
                            <div key={field.name} className={(field.type === 'textarea' || field.name === 'totpSecret') ? 'md:col-span-2' : ''}>
                                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">{field.label}</label>
                                {field.name === 'totpSecret' ? (
                                    <QrCodeInput 
                                        id={field.name}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                    />
                                ) : field.type === 'textarea' ? (
                                    <textarea 
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                        rows={4}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                                    />
                                ) : field.type === 'password' ? (
                                    <PasswordGeneratorInput 
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                    />
                                ) : (
                                    <input 
                                        type={field.type}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Seção de Campos Customizados */}
                    <div className="pt-6 border-t border-slate-800 space-y-6">
                        <div className="flex justify-between items-center">
                            <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-8 h-px bg-slate-800"></span>
                                Campos Adicionais
                            </h4>
                            <button type="button" onClick={handleAddCustomField} className="text-[10px] font-black bg-slate-800 hover:bg-slate-700 text-cyan-400 px-3 py-1.5 rounded-lg border border-slate-700 transition-all uppercase tracking-wider shadow-lg active:scale-95">
                                + Adicionar campo
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {customFields.map((field, index) => (
                                <div key={index} className="flex gap-3 group animate-in slide-in-from-left-4 duration-200">
                                    <input 
                                        placeholder="Nome do campo"
                                        value={field.name}
                                        onChange={(e) => handleCustomFieldChange(index, 'name', e.target.value)}
                                        className="flex-1 bg-slate-800/30 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-cyan-500/30 focus:outline-none transition-all"
                                    />
                                    <input 
                                        placeholder="Valor"
                                        value={field.value}
                                        onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                                        className="flex-[2] bg-slate-800/30 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-cyan-500/30 focus:outline-none transition-all"
                                    />
                                    <button type="button" onClick={() => handleRemoveCustomField(index)} className="p-3 text-slate-600 hover:text-red-400 transition-colors active:scale-90">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/20 flex justify-end gap-4">
                    <button type="button" onClick={onClose} className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-2xl transition-all border border-slate-700 active:scale-95">
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-10 py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-black rounded-2xl transition-all shadow-xl shadow-cyan-900/40 disabled:opacity-50 flex items-center gap-3 active:scale-95"
                    >
                        {loading ? <div className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full"></div> : null}
                        {mode === 'add' ? 'Finalizar Cadastro' : 'Salvar Alterações'}
                    </button>
                </footer>
            </div>
        </div>
    );
}

// --- Modal de Cadastro / Edição Completa de Clientes ---
function ClientFormModal({ mode = 'add', client, onClose, collectionName }) {
    const isEdit = mode === 'edit';
    const [loading, setLoading] = useState(false);
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    
    const [cnpj, setCnpj] = useState(formatCPFouCNPJ(client?.cnpj || '', client?.tipoPessoa || 'PJ'));
    const [tipoPessoa, setTipoPessoa] = useState(client?.tipoPessoa || 'PJ'); // 'PJ' | 'PF'
    const [name, setName] = useState(client?.name || '');
    const [nomeFantasia, setNomeFantasia] = useState(client?.nomeFantasia || '');
    const [endereco, setEndereco] = useState(client?.endereco || '');
    const [telefone, setTelefone] = useState(formatPhone(client?.telefone || ''));
    const [email, setEmail] = useState(client?.email || '');
    // E-mails para Faturamento (vários) — usados no envio de cobranças pelo Financeiro
    const [emailsFaturamento, setEmailsFaturamento] = useState(Array.isArray(client?.emailsFaturamento) ? client.emailsFaturamento : []);
    const [emailFatInput, setEmailFatInput] = useState('');
    const [emailFatErro, setEmailFatErro] = useState('');
    const addEmailsFaturamento = (texto) => {
        const lista = String(texto || '').split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
        if (!lista.length) return;
        const invalidos = lista.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        if (invalidos.length) { setEmailFatErro(`E-mail inválido: ${invalidos.join(', ')}`); return; }
        setEmailsFaturamento(prev => [...new Set([...prev, ...lista])]);
        setEmailFatInput('');
        setEmailFatErro('');
    };
    const [tipo, setTipo] = useState(client ? collectionName : 'clients'); // 'clients' (Contratos) ou 'clients-avulsos' (Avulsos)
    const [ativo, setAtivo] = useState(client?.ativo !== false); // padrão é ativo (true)
    
    const [visibleTickets, setVisibleTickets] = useState(client?.visibilidadeModulos?.tickets !== false);
    const [visibleAssets, setVisibleAssets] = useState(client?.visibilidadeModulos?.assets !== false);
    const [vhsysId, setVhsysId] = useState(client?.vhsysId || '');

    const [apiError, setApiError] = useState('');
    const [apiSuccess, setApiSuccess] = useState('');

    const handleSearchCNPJ = async () => {
        const cleanCnpj = cnpj.replace(/\D/g, '');
        if (cleanCnpj.length !== 14) {
            setApiError('O CNPJ deve conter exatamente 14 dígitos.');
            return;
        }
        setLoading(true);
        setApiError('');
        setApiSuccess('');
        try {
            const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`);
            if (!res.ok) {
                throw new Error('Empresa não encontrada ou erro no servidor da API.');
            }
            const data = await res.json();
            setName(data.razao_social || '');
            setNomeFantasia(data.nome_fantasia || data.razao_social || '');
            setEmail(data.email || '');
            
            // Tratamento do telefone
            const ddd = data.ddd_telefone_1 ? data.ddd_telefone_1.substring(0, 2) : '';
            const tel = data.ddd_telefone_1 ? data.ddd_telefone_1.substring(2) : '';
            if (ddd && tel) {
                setTelefone(formatPhone(`${ddd}${tel}`));
            } else if (data.ddd_telefone_1) {
                setTelefone(formatPhone(data.ddd_telefone_1));
            }
            setCnpj(formatCPFouCNPJ(cleanCnpj, 'PJ'));

            // Tratamento do Endereço completo formatado
            const addressParts = [
                data.logradouro && `${data.logradouro}, ${data.numero || 'S/N'}${data.complemento ? ` - ${data.complemento}` : ''}`,
                data.bairro,
                data.municipio && `${data.municipio} - ${data.uf}`,
                data.cep && `CEP ${data.cep}`
            ].filter(Boolean);
            setEndereco(addressParts.join(', '));
            setApiSuccess('Dados obtidos e preenchidos com sucesso!');
        } catch (error) {
            console.error("Erro na API CNPJ:", error);
            setApiError('Não foi possível carregar o CNPJ automaticamente. Digite manualmente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Por favor, insira o Nome do Cliente (Razão Social).');
            return;
        }

        setLoading(true);
        try {
            const cleanIncoming = cnpj.replace(/\D/g, '');
            if (cleanIncoming) {
                const contractsSnap = await getDocs(collection(db, `/artifacts/${appId}/public/data/clients`));
                const avulsosSnap = await getDocs(collection(db, `/artifacts/${appId}/public/data/clients-avulsos`));
                const allDocs = [
                    ...contractsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                    ...avulsosSnap.docs.map(d => ({ id: d.id, ...d.data() }))
                ];
                const isDuplicate = allDocs.some(c => {
                    if (isEdit && c.id === client.id) return false;
                    return c.cnpj && c.cnpj.replace(/\D/g, '') === cleanIncoming;
                });
                if (isDuplicate) {
                    alert('Este CPF/CNPJ já está cadastrado para outro cliente.');
                    setLoading(false);
                    return;
                }
            }

            const dataToSave = {
                name: name.trim(),
                nomeFantasia: (nomeFantasia || name).trim(),
                cnpj: cnpj.trim(),
                tipoPessoa,
                endereco: endereco.trim(),
                telefone: telefone.trim(),
                email: email.trim(),
                emailsFaturamento: [...new Set([...emailsFaturamento, ...String(emailFatInput || '').split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))])],
                tipo,
                ativo,
                visibilidadeModulos: {
                    tickets: visibleTickets,
                    assets: visibleAssets
                },
                vhsysId: vhsysId ? String(vhsysId) : '',
                updatedAt: new Date()
            };

            const performSave = async (updatedData) => {
                setLoading(true);
                try {
                    const targetPath = `/artifacts/${appId}/public/data/${tipo}`;

                    if (!isEdit) {
                        updatedData.createdAt = new Date();
                        await addDoc(collection(db, targetPath), updatedData);
                    } else {
                        const isTypeChanged = tipo !== collectionName;
                        if (isTypeChanged) {
                            // Mover cliente de coleção (Contrato <-> Avulso) com seus equipamentos
                            const sourcePath = `/artifacts/${appId}/public/data/${collectionName}/${client.id}`;
                            const destinationPath = `/artifacts/${appId}/public/data/${tipo}/${client.id}`;

                            const batch = writeBatch(db);
                            
                            // Copiar cliente para o novo caminho
                            batch.set(doc(db, destinationPath), updatedData);

                            // Buscar equipamentos cadastrados e copiar para o novo caminho
                            const equipmentQuery = query(collection(db, `${sourcePath}/equipment`));
                            const equipmentSnapshot = await getDocs(equipmentQuery);
                            equipmentSnapshot.forEach(equipDoc => {
                                batch.set(doc(db, `${destinationPath}/equipment/${equipDoc.id}`), equipDoc.data());
                                batch.delete(equipDoc.ref);
                            });

                            // Deletar o documento original do cliente
                            batch.delete(doc(db, sourcePath));
                            await batch.commit();
                        } else {
                            // Atualização simples
                            await updateDoc(doc(db, targetPath, client.id), updatedData);
                        }
                    }
                    onClose();
                } catch (error) {
                    console.error("Erro ao salvar cliente:", error);
                    alert("Erro ao salvar dados do cliente: " + error.message);
                } finally {
                    setLoading(false);
                }
            };

            // Sincronização automática com VHSYS
            if (cnpj.trim() && !dataToSave.vhsysId) {
                try {
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const buscarFn = httpsCallable(functions, 'buscarClientePorCnpj');
                    const searchRes = await buscarFn({ cnpj: cnpj.trim() });
                    console.log("DEBUG VHSYS buscarClientePorCnpj:", searchRes.data);
                    
                    if (searchRes.data && searchRes.data.success) {
                        const linkedId = searchRes.data.client.id_cliente;
                        dataToSave.vhsysId = String(linkedId);
                        setVhsysId(String(linkedId));
                    } else {
                        setLoading(false);
                        const debugInfo = searchRes.data?.debug ? JSON.stringify(searchRes.data.debug, null, 2) : "Sem dados adicionais";
                        console.warn("DEBUG INFO:", debugInfo);
                        showConfirmation({
                            title: "Integração VHSYS",
                            message: `Cliente não localizado no VHSYS. (Total na pág: ${searchRes.data?.debug?.total_on_page || 0})\n\nDeseja cadastrá-lo automaticamente no VHSYS?`,
                            confirmText: "Cadastrar",
                            onConfirm: async () => {
                                setLoading(true);
                                try {
                                    const cadastrarFn = httpsCallable(functions, 'cadastrarClienteNoVhsys');
                                    const createRes = await cadastrarFn({ clientData: dataToSave });
                                    if (createRes.data && createRes.data.success) {
                                        const newId = createRes.data.id_cliente;
                                        dataToSave.vhsysId = String(newId);
                                        setVhsysId(String(newId));
                                    }
                                } catch (err) {
                                    console.error("Erro na integração VHSYS:", err);
                                }
                                await performSave(dataToSave);
                            },
                            onCancel: async () => {
                                await performSave(dataToSave);
                            }
                        });
                        return;
                    }
                } catch (err) {
                    console.error("Erro na integração VHSYS:", err);
                    alert("Aviso: Não foi possível sincronizar com o VHSYS: " + err.message);
                }
            }

            await performSave(dataToSave);
        } catch (error) {
            console.error("Erro ao salvar cliente:", error);
            alert("Erro ao salvar dados do cliente: " + error.message);
            setLoading(false);
        }
    };

    const handleDelete = () => {
        showConfirmation({
            title: "Deletar Cliente",
            message: "Tem certeza de que deseja deletar este cliente? Todos os dados associados a ele serão perdidos permanentemente.",
            confirmText: "Deletar",
            confirmColor: "bg-red-500",
            onConfirm: async () => {
                setLoading(true);
                try {
                    const docRef = doc(db, `/artifacts/${appId}/public/data/${collectionName}`, client.id);
                    await deleteDoc(docRef);
                    onClose();
                } catch (err) {
                    console.error("Erro ao deletar cliente:", err);
                    alert("Erro ao deletar cliente: " + err.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };



    return (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/50 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                <header className="p-7 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-black text-white">{isEdit ? 'Editar Cadastro de Cliente' : 'Novo Cadastro de Cliente'}</h3>
                        <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Intranet Arcus • Cadastro Corporativo</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-all border border-transparent hover:border-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-8 space-y-6 custom-scrollbar">
                    {/* Seletor PF/PJ */}
                    <div className="flex items-center justify-between mb-4 bg-slate-800/30 p-5 rounded-2xl border border-slate-700/50">
                        <span className="text-xs font-semibold text-slate-350">Tipo de Inscrição</span>
                        <div className="flex bg-slate-800 rounded-lg p-0.5 border border-slate-700">
                            <button
                                type="button"
                                onClick={() => {
                                    setTipoPessoa('PJ');
                                    setCnpj('');
                                }}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${tipoPessoa === 'PJ' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                                Pessoa Jurídica (CNPJ)
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setTipoPessoa('PF');
                                    setCnpj('');
                                }}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${tipoPessoa === 'PF' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                                Pessoa Física (CPF)
                            </button>
                        </div>
                    </div>

                    {/* Linha CNPJ + API */}
                    {tipoPessoa === 'PJ' && (
                        <div className="bg-slate-800/30 p-5 rounded-2xl border border-slate-700/50 space-y-4">
                            <label className="block text-[10px] font-black text-cyan-400 uppercase tracking-[0.2em] mb-1">Preenchimento Automático via CNPJ</label>
                            <div className="flex gap-3">
                                <input 
                                    type="text"
                                    placeholder="Insira o CNPJ (Apenas números)"
                                    value={cnpj}
                                    onChange={(e) => setCnpj(formatCPFouCNPJ(e.target.value, 'PJ'))}
                                    className="flex-grow bg-slate-800/50 border border-slate-750 rounded-xl p-3.5 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 font-mono text-sm"
                                />
                                <button 
                                    type="button"
                                    onClick={handleSearchCNPJ}
                                    disabled={loading || !cnpj.trim()}
                                    className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-800 text-white font-bold py-2.5 px-6 rounded-xl transition-all shadow-lg active:scale-95 text-sm uppercase tracking-wider whitespace-nowrap"
                                >
                                    {loading ? 'Buscando...' : 'Buscar CNPJ'}
                                </button>
                            </div>
                            {apiError && <p className="text-red-400 text-xs font-medium">{apiError}</p>}
                            {apiSuccess && <p className="text-emerald-400 text-xs font-medium">{apiSuccess}</p>}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">
                                {tipoPessoa === 'PF' ? 'Nome Completo' : 'Razão Social (Nome do Cliente)'}
                            </label>
                            <input 
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={tipoPessoa === 'PF' ? "Nome da pessoa física" : "Nome oficial ou razão social"}
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">Nome Fantasia (Exibição no Sistema)</label>
                            <input 
                                type="text"
                                value={nomeFantasia}
                                onChange={(e) => setNomeFantasia(e.target.value)}
                                placeholder="Como aparecerá no sistema"
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">
                                {tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'}
                            </label>
                            <input 
                                type="text"
                                value={cnpj}
                                onChange={(e) => setCnpj(formatCPFouCNPJ(e.target.value, tipoPessoa))}
                                placeholder={tipoPessoa === 'PF' ? "000.000.000-00" : "00.000.000/0000-00"}
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">Telefone de Contato</label>
                            <input 
                                type="text"
                                value={telefone}
                                onChange={(e) => setTelefone(formatPhone(e.target.value))}
                                placeholder="Telefone ou WhatsApp"
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">Endereço Completo</label>
                            <input 
                                type="text"
                                value={endereco}
                                onChange={(e) => setEndereco(e.target.value)}
                                placeholder="Rua, número, bairro, cidade, CEP..."
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">E-mail Principal</label>
                            <input 
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="contato@cliente.com"
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all placeholder:text-slate-600 shadow-inner"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">Tipo de Cliente</label>
                            <select 
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-all shadow-inner font-bold appearance-none cursor-pointer"
                            >
                                <option value="clients">Clientes Contrato (Mensalistas)</option>
                                <option value="clients-avulsos">Clientes Avulsos (Eventuais)</option>
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">E-mails para Faturamento</label>
                            <div className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-3 shadow-inner flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-cyan-500/50">
                                {emailsFaturamento.map(e => (
                                    <span key={e} className="inline-flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-xs font-semibold rounded-lg px-2.5 py-1">
                                        {e}
                                        <button type="button" onClick={() => setEmailsFaturamento(prev => prev.filter(x => x !== e))} className="text-cyan-300/70 hover:text-red-300 font-bold" title="Remover">×</button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    value={emailFatInput}
                                    onChange={(e) => { setEmailFatInput(e.target.value); setEmailFatErro(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); addEmailsFaturamento(emailFatInput); } }}
                                    onBlur={() => emailFatInput.trim() && addEmailsFaturamento(emailFatInput)}
                                    placeholder={emailsFaturamento.length ? 'Adicionar outro e-mail...' : 'financeiro@cliente.com (Enter para adicionar)'}
                                    className="flex-1 min-w-[220px] bg-transparent p-1.5 text-white focus:outline-none placeholder:text-slate-600"
                                />
                            </div>
                            {emailFatErro
                                ? <p className="text-[11px] text-red-300 mt-1 ml-1">{emailFatErro}</p>
                                : <p className="text-[11px] text-slate-500 mt-1 ml-1">Para onde o Financeiro envia boletos, notas fiscais e cobranças. Pode ter mais de um.</p>}
                        </div>


                        <div className="md:col-span-2 border-t border-slate-800 pt-6 mt-4">
                            <h4 className="text-xs font-black text-cyan-400 uppercase tracking-[0.2em] mb-4">Visibilidade do Cliente por Módulo</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="flex items-center gap-3 p-3 bg-slate-800/20 hover:bg-slate-800/50 cursor-pointer rounded-xl transition-all border border-slate-750">
                                    <input 
                                        type="checkbox" 
                                        checked={visibleTickets} 
                                        onChange={(e) => setVisibleTickets(e.target.checked)}
                                        className="form-checkbox text-cyan-500 rounded h-5 w-5 border-slate-600 bg-slate-700" 
                                    />
                                    <div>
                                        <span className="text-white text-sm font-bold block">Módulo de Chamados</span>
                                        <span className="text-slate-500 text-xs">Exibir no seletor de chamados/tickets</span>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 bg-slate-800/20 hover:bg-slate-800/50 cursor-pointer rounded-xl transition-all border border-slate-750">
                                    <input 
                                        type="checkbox" 
                                        checked={visibleAssets} 
                                        onChange={(e) => setVisibleAssets(e.target.checked)}
                                        className="form-checkbox text-cyan-500 rounded h-5 w-5 border-slate-600 bg-slate-700" 
                                    />
                                    <div>
                                        <span className="text-white text-sm font-bold block">Módulo de Ativos</span>
                                        <span className="text-slate-500 text-xs">Exibir no seletor de gestão de ativos/equipamentos</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="md:col-span-2 border-t border-slate-800 pt-6 mt-4 bg-slate-800/10 p-5 rounded-2xl border border-slate-700/50">
                            <h4 className="text-xs font-black text-amber-400 uppercase tracking-[0.2em] mb-2">Sincronização com VHSYS</h4>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div>
                                    <p className="text-xs text-slate-400">
                                        {vhsysId ? (
                                            <>Este cliente está sincronizado com o VHSYS. Código ID: <strong className="text-white font-mono">{vhsysId}</strong></>
                                        ) : (
                                            <>Este cliente não está sincronizado com o VHSYS. Insira o {tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'} para sincronização automática ao salvar.</>
                                        )}
                                    </p>
                                </div>
                                {vhsysId && (
                                    <button
                                        type="button"
                                        onClick={() => setVhsysId('')}
                                        className="text-xs font-bold text-red-400 hover:text-red-300 underline"
                                    >
                                        Remover Vinculação
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>

                <footer className="p-8 border-t border-slate-800 bg-slate-800/20 flex justify-end gap-4">
                    {isEdit && (
                        <button type="button" onClick={handleDelete} className="px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-bold rounded-2xl transition-all mr-auto active:scale-95">
                            Deletar
                        </button>
                    )}
                    <button type="button" onClick={onClose} className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-2xl transition-all border border-slate-700 active:scale-95">
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-10 py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-black rounded-2xl transition-all shadow-xl shadow-cyan-900/40 disabled:opacity-50 flex items-center gap-3 active:scale-95"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full"></div>
                                <span>Processando...</span>
                            </>
                        ) : (
                            isEdit ? 'Salvar Alterações' : 'Finalizar Cadastro'
                        )}
                    </button>
                </footer>
            </div>

            <ConfirmationModal />
        </div>
    );
}

